import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { buildMemberHistory } from "../lib/memberApprovalHistory.js";
import { fetchCached, getCached, invalidateCached } from "../lib/requestCache.js";
import type { CorrectionRequest } from "../components/PendingAttendanceCorrectionsQueue.js";
import type { WeeklyHoursCorrectionRow } from "../components/WeeklyHoursCorrectionQueue.js";
import type { AttendanceApprovalRequest } from "../components/AttendanceApprovalQueue.js";
import type { ShiftDayRequestRow } from "../components/ShiftDayRequestQueue.js";
import type { TeamMemberDetail, MemberHistoryItem, WeeklyHoursHistoryRow } from "../pages/AttendancePage.js";

/** How far back the approval history reaches. */
const HISTORY_DAYS = 180;
const HISTORY_LIMIT = 15;

export interface TeamMemberDetailState {
  memberDetail: TeamMemberDetail | null;
  memberWeeklyCorrections: WeeklyHoursCorrectionRow[];
  memberCorrections: CorrectionRequest[];
  memberOvertime: AttendanceApprovalRequest[];
  memberShiftDayRequests: ShiftDayRequestRow[];
  memberHistory: MemberHistoryItem[];
  reload: () => void;
}

type MemberSnapshot = Omit<TeamMemberDetailState, "reload">;

const EMPTY_SNAPSHOT: MemberSnapshot = {
  memberDetail: null,
  memberWeeklyCorrections: [],
  memberCorrections: [],
  memberOvertime: [],
  memberShiftDayRequests: [],
  memberHistory: [],
};

const memberKey = (employeeId: string, weekStart: string) => `member|${employeeId}|${weekStart}`;

/**
 * Every request is settled independently: the approval queues are scoped to the
 * viewer's own approval authority, so a 403 on one of them just means this viewer
 * cannot action this person's week and that section is omitted — it must not take
 * the whole panel down.
 */
async function loadMemberSnapshot(id: string, weekStart: string): Promise<MemberSnapshot> {
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - HISTORY_DAYS * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [detail, corrections, overtime, shiftDays, weeklyCorrections, historyCorrections, historyWeekly, historyShiftDays] = await Promise.allSettled([
    api.get<TeamMemberDetail>(`/attendance/team-overview/${id}?weekStart=${weekStart}`),
    api.get<{ data: CorrectionRequest[] }>(`/attendance/corrections?status=PENDING&employeeId=${id}`),
    api.get<AttendanceApprovalRequest[]>(`/attendance/approvals?status=PENDING&employeeId=${id}`),
    api.get<ShiftDayRequestRow[]>(`/shift-day-requests?status=PENDING&employeeId=${id}`),
    api.get<WeeklyHoursCorrectionRow[]>(`/attendance/weekly-hours-corrections/pending?employeeId=${id}`),
    api.get<{ data: CorrectionRequest[] }>(`/attendance/corrections?employeeId=${id}&limit=50`),
    api.get<{ data: WeeklyHoursHistoryRow[] }>(`/attendance/weekly-hours/history?employeeId=${id}&from=${from}&to=${to}`),
    // No status filter: the decided ones are what the history is made of, and
    // one call covers both approved and declined.
    api.get<ShiftDayRequestRow[]>(`/shift-day-requests?employeeId=${id}`),
  ]);

  if (detail.status === "rejected") logError("useTeamMemberDetail.detail", detail.reason);

  return {
    memberDetail: detail.status === "fulfilled" ? detail.value.data : null,
    memberCorrections: corrections.status === "fulfilled" ? corrections.value.data.data : [],
    memberOvertime: overtime.status === "fulfilled" ? overtime.value.data : [],
    memberShiftDayRequests: shiftDays.status === "fulfilled" ? shiftDays.value.data : [],
    memberWeeklyCorrections: weeklyCorrections.status === "fulfilled" ? weeklyCorrections.value.data : [],
    memberHistory: buildMemberHistory({
      corrections: historyCorrections.status === "fulfilled" ? historyCorrections.value.data.data : [],
      weekly: historyWeekly.status === "fulfilled" ? historyWeekly.value.data.data : [],
      shiftDays: historyShiftDays.status === "fulfilled" ? historyShiftDays.value.data : [],
    }),
  };
}

// Only a snapshot with an overview is cached, so a failed open is retried next time.
function fetchMember(employeeId: string, weekStart: string) {
  return fetchCached(memberKey(employeeId, weekStart), () => loadMemberSnapshot(employeeId, weekStart), (s) => s.memberDetail !== null);
}

/**
 * Loads everything TeamMemberDetailPanel needs for one employee: their overview,
 * pending correction and weekly-hours queues, and recent approval history.
 *
 * Lifted out of DepartmentTeamBrowser so the panel can be opened from anywhere an
 * employee is listed, rather than only from a department drill-down.
 *
 * Results are cached per employee and week (see lib/requestCache.ts), so opening
 * someone again shows their data at once instead of refetching eight requests.
 */
export function useTeamMemberDetail(employeeId: string | null, weekStart: string): TeamMemberDetailState {
  const [snapshot, setSnapshot] = useState<MemberSnapshot>(EMPTY_SNAPSHOT);
  // The member and week on screen. A response for anything else is dropped.
  const activeKey = useRef<string | null>(null);

  useEffect(() => {
    const key = employeeId ? memberKey(employeeId, weekStart) : null;
    activeKey.current = key;
    if (!employeeId || !key) { setSnapshot(EMPTY_SNAPSHOT); return; }
    const hit = getCached<MemberSnapshot>(key);
    if (hit) { setSnapshot(hit); return; }
    setSnapshot(EMPTY_SNAPSHOT);
    void fetchMember(employeeId, weekStart).then((s) => { if (activeKey.current === key) setSnapshot(s); });
  }, [employeeId, weekStart]);

  // Keeps the current data on screen while the refetch runs, instead of blanking the panel.
  const reload = useCallback(() => {
    if (!employeeId) return;
    const key = memberKey(employeeId, weekStart);
    invalidateCached(key);
    void fetchMember(employeeId, weekStart).then((s) => { if (activeKey.current === key) setSnapshot(s); });
  }, [employeeId, weekStart]);

  return { ...snapshot, reload };
}
